import React, { useState } from 'react';
import {
  User,
  Bell,
  CreditCard,
  Shield,
  Palette,
  Link as LinkIcon,
  Instagram,
  Facebook,
  Youtube,
  Linkedin,
  Upload,
  Save,
} from 'lucide-react';
import Card from '../ui/CardComponent';
import Button from '../ui/ButtonComponent';
import Badge from '../ui/BadgeComponent';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import InputComponent from '../ui/InputComponent';

export default function Settings() {
  const [activeTab, setActiveTab] = useState('profile');
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    postReminders: true,
    weeklyReports: false,
    teamActivity: true,
    trendAlerts: true,
  });

  const platforms = [
    {
      id: 'instagram',
      name: 'Instagram',
      icon: Instagram,
      color: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500',
      connected: true,
      account: '@yourcompany',
    },
    {
      id: 'facebook',
      name: 'Facebook',
      icon: Facebook,
      color: 'bg-blue-600',
      connected: true,
      account: 'Your Company Page',
    },
    {
      id: 'youtube',
      name: 'YouTube',
      icon: Youtube,
      color: 'bg-red-600',
      connected: true,
      account: 'YourCompany Channel',
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: Linkedin,
      color: 'bg-blue-700',
      connected: false,
      account: null,
    },
  ];

  const billingHistory = [
    { id: 1, date: '2025-11-01', amount: '$49.00', status: 'paid', plan: 'Professional' },
    { id: 2, date: '2025-10-01', amount: '$49.00', status: 'paid', plan: 'Professional' },
    { id: 3, date: '2025-09-01', amount: '$49.00', status: 'paid', plan: 'Professional' },
  ];

  const toggleNotification = (key: string) => {
    setNotificationSettings((prev) => ({
      ...prev,
      [key]: !prev[key as keyof typeof prev],
    }));
  };

  return (
    <div className="min-h-screen md:ml-20 lg:ml-64 pt-16 md:pt-0 pb-20 md:pb-0">
      <div className="p-4 md:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-gray-900 dark:text-white mb-2">Settings</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage your account settings and preferences</p>
        </div>

        {/* Tabs */}
        <div className="mb-6 overflow-x-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
            <TabsList>
              <TabsTrigger value="profile">
                <User size={16} /> Profile
              </TabsTrigger>
              <TabsTrigger value="platforms">
                <LinkIcon size={16} /> Platforms
              </TabsTrigger>
              <TabsTrigger value="notifications">
                <Bell size={16} /> Notifications
              </TabsTrigger>
              <TabsTrigger value="branding">
                <Palette size={16} /> Branding
              </TabsTrigger>
              <TabsTrigger value="billing">
                <CreditCard size={16} /> Billing
              </TabsTrigger>
              <TabsTrigger value="security">
                <Shield size={16} /> Security
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="max-w-2xl space-y-6">
            <Card>
              <h3 className="text-gray-900 dark:text-white mb-6">Profile Information</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <img
                    src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100"
                    alt="Profile"
                    className="w-20 h-20 rounded-full object-cover"
                  />
                  <Button variant="secondary" size="sm">
                    <Upload size={16} />
                    <span>Change Avatar</span>
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="block text-sm text-gray-700 dark:text-gray-300">
                    First Name
                    <InputComponent defaultValue="John" />
                  </label>
                  <label className="block text-sm text-gray-700 dark:text-gray-300">
                    Last Name
                    <InputComponent defaultValue="doe" />
                  </label>
                </div>
                <label className="block text-sm text-gray-700 dark:text-gray-300">
                  Email
                  <InputComponent type="email" defaultValue="john.doe@company.com" />
                </label>
                <label className="block text-sm text-gray-700 dark:text-gray-300">
                  Company
                  <InputComponent defaultValue="Your Company" />
                </label>
                <label className="block text-sm text-gray-700 dark:text-gray-300">
                  Website
                  <InputComponent defaultValue="https://yourcompany.com" />
                </label>
                <Button variant="primary">
                  <Save size={16} />
                  <span>Save Changes</span>
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Platforms Tab */}
        {activeTab === 'platforms' && (
          <div className="max-w-4xl space-y-6">
            <Card>
              <h3 className="text-gray-900 dark:text-white mb-6">Connected Platforms</h3>
              <div className="space-y-4">
                {platforms.map((platform) => {
                  const Icon = platform.icon;
                  return (
                    <div
                      key={platform.id}
                      className="flex flex-col md:flex-row md:items-center justify-between p-4 rounded-xl border border-gray-200 dark:border-gray-700 gap-4"
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className={`w-12 h-12 rounded-xl ${platform.color} flex items-center justify-center flex-shrink-0`}
                        >
                          <Icon className="text-white" size={24} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-gray-900 dark:text-white">{platform.name}</h4>
                            <Badge variant={platform.connected ? 'success' : 'default'}>
                              {platform.connected ? 'Connected' : 'Not Connected'}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {platform.connected ? platform.account : 'Connect your account'}
                          </p>
                        </div>
                      </div>
                      <div>
                        {platform.connected ? (
                          <div className="flex gap-2">
                            <Button variant="ghost" size="sm">
                              Reconnect
                            </Button>
                            <Button variant="ghost" size="sm">
                              Disconnect
                            </Button>
                          </div>
                        ) : (
                          <Button variant="primary" size="sm">
                            Connect
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card>
              <h3 className="text-gray-900 dark:text-white mb-6">SEO Extension Setup</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Install our Chrome extension to get real-time SEO suggestions while composing your posts.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button variant="primary">
                  <LinkIcon size={16} />
                  <span>Install Chrome Extension</span>
                </Button>
                <Button variant="secondary">View Documentation</Button>
              </div>
            </Card>

            <Card>
              <h3 className="text-gray-900 dark:text-white mb-6">Envato Integration</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Connect your Envato account to automatically verify licenses for your media assets.
              </p>

              <label className="block text-sm text-gray-700 dark:text-gray-300">
                Envato API Key
                <InputComponent defaultValue="Enter your Envato API key" />
              </label>
              <Button variant="primary" className="mt-4">
                Connect Envato
              </Button>
            </Card>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="max-w-2xl space-y-6">
            <Card>
              <h3 className="text-gray-900 dark:text-white mb-6">Notification Preferences</h3>
              <div className="space-y-4">
                {Object.entries(notificationSettings).map(([key, value]) => (
                  <div
                    key={key}
                    className="flex items-center justify-between p-4 rounded-xl border border-gray-200 dark:border-gray-700"
                  >
                    <div>
                      <h4 className="text-gray-900 dark:text-white mb-1">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {key === 'emailNotifications' && 'Receive notifications via email'}
                        {key === 'pushNotifications' && 'Receive push notifications in browser'}
                        {key === 'postReminders' && 'Get reminders for scheduled posts'}
                        {key === 'weeklyReports' && 'Receive weekly performance reports'}
                        {key === 'teamActivity' && 'Notifications about team member activities'}
                        {key === 'trendAlerts' && 'Alerts about trending topics and hashtags'}
                      </p>
                    </div>
                    <button
                      onClick={() => toggleNotification(key)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        value ? 'bg-violet-600' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          value ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Branding Tab */}
        {activeTab === 'branding' && (
          <div className="max-w-2xl space-y-6">
            <Card>
              <h3 className="text-gray-900 dark:text-white mb-6">Brand Settings</h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Brand Logo</label>
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                      <span className="text-white text-2xl">S</span>
                    </div>
                    <Button variant="secondary" size="sm">
                      <Upload size={16} />
                      <span>Upload Logo</span>
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Primary Color</label>
                    <div className="flex gap-2">
                      <input type="color" defaultValue="#8B5CF6" className="w-12 h-10 rounded-lg cursor-pointer" />
                      <InputComponent defaultValue="#8B5CF6" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 dark:text-gray-300 mb-2">Secondary Color</label>
                    <div className="flex gap-2">
                      <input type="color" defaultValue="#EC4899" className="w-12 h-10 rounded-lg cursor-pointer" />
                      <InputComponent defaultValue="#EC4899" />
                    </div>
                  </div>
                </div>

                <label className="block text-sm text-gray-700 dark:text-gray-300">
                  Brand Name
                  <InputComponent defaultValue="SocialSync" />
                </label>
                <label className="block text-sm text-gray-700 dark:text-gray-300">
                  Tagline
                  <InputComponent defaultValue="Schedule smarter, grow faster" />
                </label>

                <Button variant="primary">
                  <Save size={16} />
                  <span>Save Brand Settings</span>
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Billing Tab */}
        {activeTab === 'billing' && (
          <div className="max-w-4xl space-y-6">
            <Card>
              <h3 className="text-gray-900 dark:text-white mb-6">Current Plan</h3>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 rounded-xl bg-violet-50 dark:bg-violet-900/20">
                <div>
                  <h4 className="text-xl text-gray-900 dark:text-white mb-1">Professional Plan</h4>
                  <p className="text-gray-600 dark:text-gray-400">$49/month</p>
                </div>
                <Badge variant="success" size="md">
                  Active
                </Badge>
              </div>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Next Billing Date</p>
                  <p className="text-gray-900 dark:text-white">December 1, 2025</p>
                </div>
                <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Payment Method</p>
                  <p className="text-gray-900 dark:text-white">•••• 4242</p>
                </div>
                <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Amount</p>
                  <p className="text-gray-900 dark:text-white">$49.00</p>
                </div>
              </div>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Button variant="primary">Upgrade Plan</Button>
                <Button variant="ghost">Cancel Subscription</Button>
              </div>
            </Card>

            <Card>
              <h3 className="text-gray-900 dark:text-white mb-6">Payment Method</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-8 bg-gradient-to-r from-blue-600 to-blue-400 rounded flex items-center justify-center">
                      <CreditCard className="text-white" size={20} />
                    </div>
                    <div>
                      <p className="text-gray-900 dark:text-white">Visa ending in 4242</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Expires 12/2026</p>
                    </div>
                  </div>
                  <Badge variant="success">Default</Badge>
                </div>
                <Button variant="secondary">
                  <CreditCard size={16} />
                  <span>Add Payment Method</span>
                </Button>
              </div>
            </Card>

            <Card>
              <h3 className="text-gray-900 dark:text-white mb-6">Billing History</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 text-sm text-gray-600 dark:text-gray-400">Date</th>
                      <th className="text-left py-3 px-4 text-sm text-gray-600 dark:text-gray-400">Description</th>
                      <th className="text-left py-3 px-4 text-sm text-gray-600 dark:text-gray-400">Amount</th>
                      <th className="text-left py-3 px-4 text-sm text-gray-600 dark:text-gray-400">Status</th>
                      <th className="text-right py-3 px-4 text-sm text-gray-600 dark:text-gray-400">Invoice</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billingHistory.map((item) => (
                      <tr key={item.id} className="border-b border-gray-200 dark:border-gray-700 last:border-0">
                        <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">{item.date}</td>
                        <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">{item.plan} Plan</td>
                        <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">{item.amount}</td>
                        <td className="py-3 px-4">
                          <Badge variant="success" size="sm">
                            {item.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Button variant="ghost" size="sm">
                            Download
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="max-w-2xl space-y-6">
            <Card>
              <h3 className="text-gray-900 dark:text-white mb-6">Change Password</h3>
              <div className="space-y-4">
                <label className="block text-sm text-gray-700 dark:text-gray-300">
                  Current Password
                  <InputComponent type="password" />
                </label>
                <label className="block text-sm text-gray-700 dark:text-gray-300">
                  New Password
                  <InputComponent type="password" />
                </label>
                <label className="block text-sm text-gray-700 dark:text-gray-300">
                  Confirm New Password
                  <InputComponent type="password" />
                </label>
                <Button variant="primary">Update Password</Button>
              </div>
            </Card>

            <Card>
              <h3 className="text-gray-900 dark:text-white mb-6">Two-Factor Authentication</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Add an extra layer of security to your account by enabling two-factor authentication.
              </p>
              <Button variant="secondary">Enable 2FA</Button>
            </Card>

            <Card>
              <h3 className="text-gray-900 dark:text-white mb-6">Active Sessions</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                  <div>
                    <p className="text-gray-900 dark:text-white mb-1">Chrome on MacOS • San Francisco, US</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Last active 2 hours ago</p>
                  </div>
                  <Badge variant="success">Current</Badge>
                </div>
                <div className="flex items-center justify-between p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                  <div>
                    <p className="text-gray-900 dark:text-white mb-1">Safari on iPhone • San Francisco, US</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Last active 1 day ago</p>
                  </div>
                  <Button variant="ghost" size="sm">
                    Revoke
                  </Button>
                </div>
              </div>
            </Card>

            <Card>
              <h3 className="text-gray-900 dark:text-white mb-6">Danger Zone</h3>
              <div className="space-y-4">
                <div className="p-4 rounded-xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20">
                  <h4 className="text-red-900 dark:text-red-200 mb-2">Delete Account</h4>
                  <p className="text-sm text-red-700 dark:text-red-300 mb-4">
                    Once you delete your account, there is no going back. Please be certain.
                  </p>
                  <Button variant="danger" size="sm">
                    Delete Account
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
